import { useAuthStore } from './auth';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'https://backend.iceking.wtf/api';
const API_AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL || 'https://backend.iceking.wtf';

const getAuthHeaders = () => {
  const token = useAuthStore.getState().token;
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  calendarId?: CalendarId | null;
  calendarName?: string | null;
}

export type CalendarId = 'primary' | 'taiwan holiday' | 'us holiday' | 'work' | 'HYTE';

export const CALENDAR_IDS: CalendarId[] = ['primary', 'taiwan holiday', 'us holiday', 'work', 'HYTE'];

const getCalendarId = (calendarId: CalendarId): string => {
  switch (calendarId) {
    case 'primary': return 'j4092627@gmail.com';
    case 'taiwan holiday': return 'en.taiwan#holiday@group.v.calendar.google.com';
    case 'us holiday': return 'zh-tw.usa#holiday@group.v.calendar.google.com';
    case 'work': return '0iulle0n3qocecfsic8765to1vcv1f3p@import.calendar.google.com';
    case 'HYTE': return 'dc47bb3c7655796fac90d133810540112000ca8ccae78e08a40eadaeef84fc46@group.calendar.google.com';
    default: return 'j4092627@gmail.com';
  }
};

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...getAuthHeaders(),
});

export const calendarService = {
  async ensureValidToken(): Promise<void> {
    const authStore = useAuthStore.getState();
    const { googleToken, googleTokenExpiry } = authStore;
    
    // If no token, can't proceed
    if (!googleToken) {
      throw new Error('GOOGLE_AUTH_REQUIRED');
    }
    
    // Check if token is expired (or will expire in next 5 minutes)
    const isExpired = !googleTokenExpiry || 
      Date.now() >= (googleTokenExpiry - 7 * 60 * 1000);
      
    if (isExpired) {
      try {
        await authStore.refreshGoogleToken();
      } catch (error) {
        console.error('Error refreshing token:', error);
        throw new Error('GOOGLE_AUTH_REQUIRED');
      }
    }
  },

  async getEvents(timeMin?: string, timeMax?: string, calendarId?: CalendarId): Promise<CalendarEvent[]> {
    try {
      // Ensure we have a valid token before making the request
      await this.ensureValidToken();
      
      // Prepare the URL with query parameters
      const params = new URLSearchParams();
      if (timeMin) params.append('timeMin', timeMin);
      if (timeMax) params.append('timeMax', timeMax);
      if (calendarId) params.append('calendarId', getCalendarId(calendarId));
      
      const response = await fetch(`${API_URL}/calendar/events?${params.toString()}`, {
        headers: getHeaders(),
      });
      
      if (!response.ok) {
        // Check if the error is due to authentication
        if (response.status === 401 || response.status === 403) {
          console.log('Authentication error, Google token needs to be refreshed');
          throw new Error('GOOGLE_AUTH_REQUIRED');
        }
        throw new Error('Failed to fetch events');
      }
      return response.json();
    } catch (error: any) {
      console.error('Error fetching events:', error);
      
      // Handle authentication errors by prompting for re-authentication
      if (error.message === 'GOOGLE_AUTH_REQUIRED') {
        const authStore = useAuthStore.getState();
        try {
          // Try to re-authenticate
          await authStore.refreshOrAuthenticate();
          // If successful, retry the request
          return this.getEvents(timeMin, timeMax, calendarId);
        } catch (authError) {
          console.error('Re-authentication failed:', authError);
          window.location.href = "/login";
        }
      }
      
      throw error;
    }
  },

  async setAccessToken(token: string) {
    console.log("set access token");
    try {
      const response = await fetch(`${API_AUTH_BASE_URL}/auth/setToken?token=${token}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'}
      });
      
      if (!response.ok) {
        throw new Error('Failed to set token');
      }
      return response.json();
    } catch (error) {
      console.error('Error setting access token:', error);
      throw error;
    }
  },
  
  async createEvent(event: CalendarEvent) {
    try {
      await this.ensureValidToken();
      
      const response = await fetch(`${API_URL}/calendar/create-events`, {
        method: 'POST', 
        headers: getHeaders(),
        body: JSON.stringify(event),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create event');
      }
      return response.json();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  async updateEvent(event: CalendarEvent) {
    try {
      await this.ensureValidToken();
      
      const response = await fetch(`${API_URL}/calendar/events/${event.id}`, {
        method: 'PUT', 
        headers: getHeaders(),
        body: JSON.stringify(event),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update event');
      }
      return response.json();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }
};